import { CarWash } from './CarWash';
import { DriveThroughGate } from './DriveThroughGate';
import { FillTheGap } from './FillTheGap';
import { FindTheSpot } from './FindTheSpot';
import { MatchTheCar } from './MatchTheCar';
import { ParkTheCar } from './ParkTheCar';
import { ParkingRush } from './ParkingRush';
import { ParkingSlotPuzzle } from './ParkingSlotPuzzle';
import { RaiseTheBarrier } from './RaiseTheBarrier';
import { TrafficLight } from './TrafficLight';
import { pickOne } from '@/modules/captcha/shared/puzzle-utils';
import type { PuzzleDefinition } from './puzzle.types';

/**
 * The ten challenges in rotation, keyed by their Claude Design identifiers.
 *
 * ## Invariants:
 * - Ids are the design's own ("1a", "2o", …) and must not be renamed. Why: they are how the
 *   design source, the review thread and any future bug report all refer to a given puzzle;
 *   renaming them to something more descriptive breaks the only shared vocabulary.
 * - The mix is deliberate — four tap-a-tile, two drag, two slide, one comprehension, one moving
 *   target. Why: a rotation that is all tap-a-tile is one solved problem repeated ten ways, and
 *   offers a scripted client nothing to distinguish. Keep the spread when adding or removing.
 */
export const PUZZLES: readonly PuzzleDefinition[] = [
  { id: '1a', promptKey: 'parkTheCarPrompt', Component: ParkTheCar },
  { id: '1c', promptKey: 'raiseTheBarrierPrompt', Component: RaiseTheBarrier },
  { id: '1d', promptKey: 'fillTheGapPrompt', Component: FillTheGap },
  { id: '2b', promptKey: 'findTheSpotPrompt', Component: FindTheSpot },
  { id: '2c', promptKey: 'driveThroughGatePrompt', Component: DriveThroughGate },
  { id: '2f', promptKey: 'trafficLightPrompt', Component: TrafficLight },
  { id: '2h', promptKey: 'matchTheCarPrompt', Component: MatchTheCar },
  { id: '2j', promptKey: 'findSlotPrompt', Component: ParkingSlotPuzzle },
  { id: '2k', promptKey: 'tapColoredCarPrompt', Component: CarWash },
  { id: '2o', promptKey: 'tapColoredCarPrompt', Component: ParkingRush },
];

/**
 * Picks a challenge at random.
 *
 * Uniform over all ten with no memory of the last pick, so the same puzzle can legitimately come
 * up twice running. Suppressing that would make the sequence predictable in exactly the way the
 * randomisation exists to prevent — "never the same twice" tells a caller what it is NOT about
 * to get.
 */
export function pickRandomPuzzle(): PuzzleDefinition {
  return pickOne(PUZZLES);
}

export type { PuzzleDefinition, PuzzleProps } from './puzzle.types';
